import { log, LogCategory } from '../../../../../map-editor-shared/lib/logger'
import { FileStorageService } from '../../../../../services/FileStorageService'
import { DxfParserService, SelectedLayerInfo } from '../../../../../services/DxfParserService'
import { FileInfo } from '../../../../../models/FileStorage'
import type { ViewMode } from '../types'
import { useCsvRuntimeStore } from '../../../model/csvStore'
import { useFloorStore } from '../../../../../map-editor-shared/store/floorStore'
import { useProjectStore } from '../../../../../map-editor-shared/store/projectStore'
import { RecoverySelection, recoveryCsv } from 'components/parking-agent/recoveryCsv'
import { mergeRecoveryCsv } from 'components/parking-agent/mergeRecoveryCsv'

interface UseLayerSelectionDeps {
  setViewMode: (mode: ViewMode) => void
  setUploadState: (state: any) => void
  setFile: (file: File) => void
  parseFile: () => Promise<void>
  setDownloadError: (error: string | null) => void
  setDownloadMessage: (message: string) => void
  setShowLayerModal: (show: boolean) => void
  setSelectedDxfFile: (file: FileInfo | null) => void
  setAnalyzedLayers: (layers: any[]) => void
  selectedDxfFile: FileInfo | null
  onMappingRequired?: () => void
  currentProjectId: number | null
  siteProjectId: number | null
}

export function useLayerSelection(deps: UseLayerSelectionDeps) {
  const {
    setViewMode,
    setUploadState,
    setFile,
    parseFile,
    setDownloadError,
    setDownloadMessage,
    setShowLayerModal,
    setSelectedDxfFile,
    setAnalyzedLayers,
    selectedDxfFile,
    onMappingRequired,
    siteProjectId,
  } = deps

  const handleLayerSelectionConfirm = async (selectedLayers: SelectedLayerInfo[], recovery?: RecoverySelection) => {
    setShowLayerModal(false)

    if (!selectedDxfFile || !siteProjectId || (!selectedLayers.length && !recovery?.candidates.length)) return

    try {
      if (!selectedLayers.length && recovery?.candidates.length) {
        if (!useFloorStore.getState().currentFloor) {
          const lotId = useProjectStore.getState().currentLot
          if (!lotId) throw new Error('프로젝트를 먼저 선택해주세요.')
          useFloorStore.getState().addFloor(lotId)
        }
        useCsvRuntimeStore.getState().setCctvIps([])
        setFile(recoveryCsv(recovery.candidates, recovery.sourceHash))
        setViewMode('parsing')
        await parseFile()
        setViewMode('parsed')
        onMappingRequired?.()
        return
      }
      setViewMode('downloading')
      setDownloadMessage('선택한 레이어를 CSV로 변환 중...')
      setUploadState({
        status: 'uploading',
        progress: 30,
      })

      // Step 1: Convert DXF to CSV with selected layers (name + type)
      log.info(LogCategory.CSV, 'Starting DXF conversion with selected layers', {
        dxfFilename: selectedDxfFile.filename,
        selectedLayers: selectedLayers.map((l) => `${l.name} (${l.suggestedType})`),
      })

      // DXF source and CSV output both live at the flat {projectId}/uploads/{cad|csv}/ path.
      // mapId 는 보내지 않는다(=0). 이 배포는 DXF/CSV 를 flat 경로
      // {projectId}/uploads/{cad|csv}/ 에 저장하므로 mapId 를 넘기면 파일 경로가
      // 중첩 경로로 어긋나 "파일 없음" 이 된다. CCTV IP prefix 는 백엔드가
      // projectId(=현장 site_project_id)로 현장 단위 조회한다.
      const conversionResult = await DxfParserService.convertDxfToCsv(
        siteProjectId,
        selectedDxfFile.filename,
        selectedLayers
      )

      if (!conversionResult.success || !conversionResult.data) {
        throw new Error(conversionResult.message || 'DXF 변환 실패')
      }

      const csvFilename = conversionResult.data.filename
      log.info(LogCategory.CSV, 'DXF conversion complete', { csvFilename })

      // Stash CCTV IPs (meter coords) so EditorPage can auto-wire them to the
      // CCTV elements once they render. Empty array when backend returned none
      // (no cctv_ip_prefix on the project, or no labels matched).
      const cctvIps = conversionResult.data.cctvIps ?? []
      useCsvRuntimeStore.getState().setCctvIps(cctvIps)
      log.info(LogCategory.CSV, 'CCTV IPs stashed for auto-apply', { count: cctvIps.length })

      // 주차 형상 품질 진단(비파괴): 비정형(기울어진) 주차칸이 있으면 경고를 노출.
      // 레이어 매핑 오류나 도면 자체 문제를 사용자가 바로 확인하도록 유도.
      const quality = conversionResult.data.parkingQualityReport
      if (quality && quality.suspicious > 0) {
        log.warn(LogCategory.CSV, 'Suspicious parking polygons detected', {
          suspicious: quality.suspicious,
          total: quality.total,
          samples: quality.items.slice(0, 5),
        })
        setDownloadMessage(
          `⚠️ 주차 ${quality.total}개 중 ${quality.suspicious}개가 비정형(기울어짐/비직각) — 레이어 매핑/도면 확인 권장`
        )
      } else {
        setDownloadMessage('변환된 CSV 다운로드 중...')
      }
      setUploadState({
        status: 'uploading',
        progress: 60,
      })

      // Step 2: Download the generated CSV file from the flat path.
      const blob = await FileStorageService.downloadFile(String(siteProjectId), 'csv', csvFilename)
      log.info(LogCategory.CSV, 'CSV file downloaded', { size: blob.size })

      setDownloadMessage('CSV 데이터 처리 중...')
      setUploadState({
        status: 'uploading',
        progress: 80,
      })

      // Step 3: Create File object from blob
      const convertedFile = new File([blob], csvFilename, { type: 'text/csv' })
      const csvFile = recovery?.candidates.length
        ? await mergeRecoveryCsv(convertedFile, recovery, conversionResult.data.coordinateOffset)
        : convertedFile

      setDownloadMessage('CSV 분석 중...')
      setUploadState({
        status: 'uploading',
        progress: 90,
      })

      // Step 4: Set file and trigger parsing
      setFile(csvFile)
      setViewMode('parsing')

      // Step 5: Parse the file
      await parseFile()

      log.info(LogCategory.CSV, 'CSV file parsed successfully from DXF conversion')
      setViewMode('parsed')

      // Trigger layer mapping modal after successful parsing
      if (onMappingRequired) {
        onMappingRequired()
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'CAD 파일 처리에 실패했습니다.'
      log.error(LogCategory.CSV, 'CAD processing failed', { error: errorMessage })

      setDownloadError(errorMessage)
      setViewMode('error')
      setUploadState({
        status: 'error',
        message: errorMessage,
      })
    }
  }

  const handleLayerSelectionCancel = () => {
    setShowLayerModal(false)
    setSelectedDxfFile(null)
    setAnalyzedLayers([])
    setViewMode('idle')
  }

  return { handleLayerSelectionConfirm, handleLayerSelectionCancel }
}
